import DOMPurify from 'dompurify';

/**
 * Nettoie et sécurise les entrées utilisateur contre les injections XSS
 */
export const sanitizeInput = (input: string): string => {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // Pas de HTML autorisé
    ALLOWED_ATTR: [], // Pas d'attributs autorisés
    KEEP_CONTENT: true // Garder le texte
  }).trim();
};

/**
 * Valide un email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valide un numéro de téléphone français
 */
export const isValidPhone = (phone: string): boolean => {
  // Accepte les formats: 0676825228, +33676825228, 06 76 82 52 28, etc.
  const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
  return phoneRegex.test(phone);
};

/**
 * Valide que le texte ne contient pas de patterns suspects
 */
export const containsSuspiciousContent = (text: string): boolean => {
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // onclick, onerror, etc.
    /<iframe/i,
    /eval\(/i,
    /expression\(/i,
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(text));
};

/**
 * Valide la longueur d'un texte
 */
export const isValidLength = (text: string, min: number, max: number): boolean => {
  const length = text.trim().length;
  return length >= min && length <= max;
};

/**
 * Nettoie un objet de données de formulaire
 */
export const sanitizeFormData = (data: Record<string, string>): Record<string, string> => {
  const sanitized: Record<string, string> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Détecte les tentatives de spam
 */
export const isLikelySpam = (data: Record<string, string>): boolean => {
  const text = Object.values(data).join(' ').toLowerCase();
  
  const spamKeywords = [
    'viagra', 'cialis', 'casino', 'lottery', 'bitcoin',
    'crypto', 'investment', 'make money fast', 'click here',
    'limited time', 'act now', 'free money'
  ];
  
  // Détecte trop de liens
  const urlCount = (text.match(/https?:\/\//g) || []).length;
  if (urlCount > 3) return true;
  
  // Détecte les mots spam
  return spamKeywords.some(keyword => text.includes(keyword));
};

/**
 * Limite le taux d'envoi (rate limiting côté client)
 */
const submissionTimes: number[] = [];

export const checkRateLimit = (maxSubmissions: number, timeWindowMinutes: number): boolean => {
  const now = Date.now();
  const timeWindow = timeWindowMinutes * 60 * 1000;
  
  // Nettoyer les anciennes entrées
  const recentSubmissions = submissionTimes.filter(time => now - time < timeWindow);
  
  if (recentSubmissions.length >= maxSubmissions) {
    return false; // Rate limit dépassé
  }
  
  // Ajouter la nouvelle soumission
  submissionTimes.push(now);
  
  // Garder seulement les récentes
  submissionTimes.splice(0, submissionTimes.length - maxSubmissions);
  
  return true;
};
